import type { RcFile, UploadFile } from 'antd/es/upload'

import { uploadFiles } from 'src/utils/uploadFiles'

import { usePresignedUpload } from './usePresignedUpload'

/**
 * The whole presigned dance in one call: reserve an attachment, PUT the bytes to
 * the signed URL, notify the webhook. `usePresignedUpload` alone only reserves
 * the attachment — the bytes never leave the browser without `uploadFiles`.
 *
 * Returns the attachment id, which is what write endpoints take. Read endpoints
 * hand back the stored file URL themselves, so nothing here fetches it; for a
 * pre-save preview build an object URL from the file you already hold.
 */
export const useAttachmentUpload = () => {
  const { uploadFile, submitWebhook } = usePresignedUpload()

  const upload = async (
    file: RcFile,
    /** Workspace the attachment belongs to. Omit only for account-level files. */
    organizationId?: number,
    options?: { isPublic?: boolean }
  ): Promise<number> => {
    const reserved = await uploadFile(file, organizationId, options)

    await uploadFiles(
      [
        {
          uid: file.uid,
          name: file.name,
          status: 'done',
          originFileObj: file,
          response: {
            upload_url: reserved.upload_url,
            webhook_data: reserved.webhook_data
          }
        } as UploadFile
      ],
      { submitWebhook }
    )

    return reserved.id
  }

  return { upload }
}
